(function () {
  'use strict';

  const FILTER_UPDATE_EVENT = 'bf:filter:updated';

  /**
   * BF Infinite Scroll
   * Loads next pages of collection products automatically as the user scrolls,
   * using the Shopify Section Rendering API to fetch partial section HTML.
   *
   * Required elements in the section template:
   *  - .products-grid            — container where product cards are appended
   *  - #infinite-scroll-meta    — hidden element with data-section-id and data-last-page
   *  - #infinite-scroll-sentinel — invisible sentinel observed by IntersectionObserver
   *
   * Reacts to the custom event "bf:filter:updated" (dispatched by bf-collection-filters.js)
   * to reinitialize after a filter/sort change replaces the DOM.
   */
  class BFInfiniteScroll {
    #currentPage;
    #lastPage;
    #loaderDelay;
    #sectionId;
    #grid;
    #sentinel;
    #loader;
    #observer;
    #pages = new Map();
    #isLoading = false;

    constructor() {
      this.#init();

      // Reinitialize whenever filters/sort update the DOM
      document.addEventListener(FILTER_UPDATE_EVENT, () => this.#reinit());
    }

    // ─── Init / Reinit ──────────────────────────────────────────────────────

    #init() {
      const grid = document.querySelector('.products-grid');
      const sentinel = document.getElementById('infinite-scroll-sentinel');
      const meta = document.getElementById('infinite-scroll-meta');

      if (!grid || !sentinel || !meta) return;

      this.#grid = grid;
      this.#sentinel = sentinel;
      this.#loader = document.getElementById('infinite-scroll-loader');
      this.#sectionId = meta.dataset.sectionId;
      this.#lastPage = parseInt(meta.dataset.lastPage, 10) || 1;
      this.#loaderDelay = parseInt(meta.dataset.loaderDelay, 10) || 600;
      this.#currentPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;

      // If the user lands on ?page=N (e.g. via shared link or back-navigation),
      // silently prepend pages 1…N-1 so the full product list is visible.
      if (this.#currentPage > 1) {
        this.#restorePreviousPages();
      }

      if (this.#currentPage >= this.#lastPage) {
        this.#sentinel.remove();
        return;
      }

      this.#setupObserver();
      this.#prefetchPage(this.#currentPage + 1);
    }

    #reinit() {
      // Teardown previous observer so it doesn't fire on a stale sentinel
      this.#teardown();

      // Clear page cache since filter context changed
      this.#pages.clear();
      this.#isLoading = false;

      // Re-read DOM after the filter update swapped bf-products-area
      this.#init();
    }

    // ─── Restore on direct ?page=N access ───────────────────────────────────

    async #restorePreviousPages() {
      // Fetch all missing pages in parallel
      const fetches = [];
      for (let p = 1; p < this.#currentPage; p++) {
        fetches.push(this.#fetchPage(p));
      }
      const results = await Promise.all(fetches);

      const fragment = document.createDocumentFragment();
      for (const html of results) {
        if (!html) continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.querySelector('.products-grid');
        if (!grid) continue;
        Array.from(grid.children).forEach((item) => fragment.appendChild(item.cloneNode(true)));
      }

      if (!fragment.childElementCount) return;

      // Anchor scroll so the server-rendered products stay in view
      const anchor = this.#grid.firstElementChild;
      const topBefore = anchor?.getBoundingClientRect().top ?? 0;

      this.#grid.insertBefore(fragment, this.#grid.firstChild);

      if (anchor) {
        const topAfter = anchor.getBoundingClientRect().top;
        window.scrollBy({ top: topAfter - topBefore, behavior: 'instant' });
      }
    }

    // ─── IntersectionObserver ───────────────────────────────────────────────

    #setupObserver() {
      if (this.#observer) this.#observer.disconnect();

      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#loadNextPage();
            }
          }
        },
        { rootMargin: '300px' }
      );

      this.#observer.observe(this.#sentinel);
    }

    // ─── Fetching ───────────────────────────────────────────────────────────

    async #fetchPage(page) {
      const cached = this.#pages.get(page);
      if (cached) return cached;

      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      url.searchParams.set('section_id', this.#sectionId);
      url.hash = '';

      try {
        const res = await fetch(url.toString());
        if (!res.ok) return null;
        const html = await res.text();
        this.#pages.set(page, html);
        return html;
      } catch {
        return null;
      }
    }

    async #prefetchPage(page) {
      if (page <= this.#lastPage) {
        await this.#fetchPage(page);
      }
    }

    // ─── Rendering ──────────────────────────────────────────────────────────

    async #loadNextPage() {
      if (this.#isLoading) return;

      const next = this.#currentPage + 1;

      if (next > this.#lastPage) {
        this.#teardown();
        return;
      }

      this.#isLoading = true;
      this.#loader?.classList.add('is-visible');

      const [html] = await Promise.all([
        this.#fetchPage(next),
        new Promise((r) => setTimeout(r, this.#loaderDelay)),
      ]);

      if (!html) {
        this.#loader?.classList.remove('is-visible');
        this.#isLoading = false;
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('.products-grid');

      if (newGrid && newGrid.children.length > 0) {
        const fragment = document.createDocumentFragment();
        Array.from(newGrid.children).forEach((item) => {
          fragment.appendChild(item.cloneNode(true));
        });
        this.#grid.appendChild(fragment);

        this.#currentPage = next;

        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('page', next);
        history.pushState('', '', newUrl.toString());
      }

      this.#loader?.classList.remove('is-visible');
      this.#isLoading = false;

      if (next >= this.#lastPage) {
        this.#teardown();
        return;
      }

      const idleCb = window.requestIdleCallback || ((cb) => setTimeout(cb, 100));
      idleCb(() => this.#prefetchPage(next + 1));
    }

    // ─── Cleanup ────────────────────────────────────────────────────────────

    #teardown() {
      if (this.#observer) {
        this.#observer.disconnect();
        this.#observer = null;
      }
      // Remove sentinel only if it still exists in the DOM (might have been
      // replaced by the filter update swap of bf-products-area)
      if (this.#sentinel && this.#sentinel.isConnected) {
        this.#sentinel.remove();
      }
      this.#sentinel = null;
      this.#grid = null;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new BFInfiniteScroll());
  } else {
    new BFInfiniteScroll();
  }
})();
